/**
 * Home-equity release — what a Reverse Mortgage or the Home Equity Access Scheme puts in the client's
 * hands this year, and what the means tests make of it, as a pure function.
 *
 * Both strategies turn home equity into spendable money without selling the home, and neither is repaid
 * out of cashflow: the balance compounds and is settled from the estate (that half is the projection
 * engine's loanPlugin, which owns these loans' balances). What this assesses is the near side of them —
 * the money arriving, and its means-test consequences:
 *
 *   - REGULAR PAYMENTS (either scheme) are cash income for the year: the instalment annualised at its
 *     own frequency. They are NOT an assessable asset — the money is drawn as it is spent.
 *   - A REVERSE MORTGAGE LUMP SUM is different, because what happens to it decides whether it is
 *     assessed. Spent on a RAD it is exempt (a RAD always is); left over it is cash the client now holds,
 *     and so both an assessable asset and a deemed financial one. It is never income: borrowing is not
 *     earning.
 *   - The HEAS pension reduction is structurally present and always zero. That is deliberate, not
 *     unfinished: L-AP2 in the legislation validation register records that HEAS payments are a
 *     non-assessable government loan which must NOT reduce the pension, and the field is kept so the
 *     old dollar-for-dollar deduction cannot quietly come back. See homeEquityReleaseFixes.test.ts.
 *
 * The other deliberate zero is L-RM1: a lump sum NOT going into a RAD reports no proceeds here, because
 * the caller that models such a strategy has already added the money to the client's bank balance
 * (the AgedCareAnalyser `simulatedFactFind` contract). Assessing it again would count it twice.
 *
 * Extracted from `calculateFinancials`'s "--- 3. Reverse Mortgage & HEAS Calculations ---" block, which
 * still calls it — through the injectable {@link HomeEquityReleaseAssessor} seam, so the projection
 * engine can hand in the assessor owned by its loanPlugin, which is the plugin that already owns these
 * two loans' compounding balances.
 */

import type { FactFind } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';

/** The cadences a home-equity payment can be drawn at. Anything not fortnightly is treated as monthly. */
export type HomeEquityPaymentFrequency = 'weekly' | 'fortnightly' | 'monthly';

/**
 * A regular home-equity payment as an annual figure: 26 fortnightly instalments, or 12 monthly ones.
 *
 * The single definition of an annualisation the engine needs in two places — here, for the year's cash
 * income, and in the loanPlugin, which adds the same amount to the loan balance it accrues against.
 * They must agree: the money the client receives is exactly the money the debt grows by.
 */
export function annualiseHomeEquityPayment(paymentAmount: number, frequency: string | undefined): number {
    const amount = paymentAmount || 0;
    return frequency === 'fortnightly' ? amount * 26 : amount * 12;
}

/** The position a home-equity strategy is assessed against. */
export interface HomeEquityReleaseInput {
    /** Read for the two strategies' details, and for `radInput` — the RAD a lump sum may be spent on. */
    strategyOptions: StrategyOptions;
    /** Read for its own `radInput` only, as the fallback when `strategyOptions` carries none. */
    factFind: FactFind;
    /** Assessable assets as they stand after deeming, which any assessed proceeds are added to. */
    assessableAssets: number;
    /** The deeming base likewise. Note the caller deems BEFORE this runs — see {@link HomeEquityReleaseAssessment.financialAssetsForDeeming}. */
    financialAssetsForDeeming: number;
}

/** What the strategy produced. */
export interface HomeEquityReleaseAssessment {
    /** Reverse-mortgage regular payments for the year (annual). 0 for a lump-sum loan. */
    reverseMortgageIncome: number;
    /**
     * Lump-sum proceeds that end up assessable — the part not spent on a RAD, and only where the strategy
     * is spending on one at all (see the L-RM1 note in this module's header). Already inside both totals
     * below.
     */
    reverseMortgageLoanProceeds: number;
    /** HEAS payments for the year (annual). */
    heasIncome: number;
    /** Always 0 — HEAS does not reduce the pension (L-AP2). Kept as a field so the old behaviour cannot return. */
    heasAgePensionReduction: number;
    /** {@link HomeEquityReleaseInput.assessableAssets} with {@link reverseMortgageLoanProceeds} included. */
    assessableAssets: number;
    /**
     * {@link HomeEquityReleaseInput.financialAssetsForDeeming} with {@link reverseMortgageLoanProceeds}
     * included. The caller has already struck its deemed income by this point, so proceeds enlarge the
     * base for anything reading it afterwards but are not themselves deemed in the year the loan is
     * drawn. Long-standing engine behaviour, pinned by the golden master.
     */
    financialAssetsForDeeming: number;
}

/**
 * Assesses a home-equity-release strategy. Injected into `calculateFinancials` so a caller can own the
 * rules — see {@link calculateHomeEquityRelease}, the default, and the projection engine's loanPlugin.
 */
export type HomeEquityReleaseAssessor = (input: HomeEquityReleaseInput) => HomeEquityReleaseAssessment;

/**
 * Works out this year's home-equity-release income and any assessable lump-sum proceeds.
 *
 * Nothing happens unless a strategy is switched on AND carries its details — an empty `strategyOptions`
 * (every standalone fact-find call) returns the position untouched.
 */
export const calculateHomeEquityRelease: HomeEquityReleaseAssessor = (input: HomeEquityReleaseInput): HomeEquityReleaseAssessment => {
    const { strategyOptions, factFind } = input;

    let reverseMortgageIncome = 0;
    let reverseMortgageLoanProceeds = 0;
    let heasIncome = 0;
    const heasAgePensionReduction = 0;

    if (strategyOptions.reverseMortgage && strategyOptions.reverseMortgageDetails) {
        const rmDetails = strategyOptions.reverseMortgageDetails;
        const useForRad = rmDetails.useForRad || false;

        if (rmDetails.loanStructure === 'lump_sum') {
            const loanAmount = rmDetails.loanAmount || 0;
            const establishmentFees = rmDetails.establishmentFees || 0;
            const netProceeds = Math.max(0, loanAmount - establishmentFees);
            reverseMortgageIncome = 0;
            if (useForRad) {
                // Whatever the RAD absorbs is exempt; only the remainder is a countable asset. No RAD
                // figure at all means the whole loan was raised for it, so nothing is left over.
                const radCap = strategyOptions.radInput || factFind?.radInput || 0;
                const radPortionApplied = radCap > 0 ? Math.min(netProceeds, radCap) : netProceeds;
                reverseMortgageLoanProceeds = Math.max(0, netProceeds - radPortionApplied);
            } else {
                // L-RM1: the caller has already banked these proceeds — see this module's header.
                reverseMortgageLoanProceeds = 0;
            }
        } else if (rmDetails.loanStructure === 'regular_payments') {
            reverseMortgageIncome = annualiseHomeEquityPayment(rmDetails.regularPaymentAmount || 0, rmDetails.regularPaymentFrequency || 'fortnightly');
            reverseMortgageLoanProceeds = 0;
        }
    }

    if (strategyOptions.heas && strategyOptions.heasDetails) {
        heasIncome = annualiseHomeEquityPayment(strategyOptions.heasDetails.paymentAmount || 0, strategyOptions.heasDetails.paymentFrequency || 'fortnightly');
    }

    const assessProceeds = reverseMortgageLoanProceeds > 0;
    return {
        reverseMortgageIncome,
        reverseMortgageLoanProceeds,
        heasIncome,
        heasAgePensionReduction,
        assessableAssets: assessProceeds ? input.assessableAssets + reverseMortgageLoanProceeds : input.assessableAssets,
        financialAssetsForDeeming: assessProceeds ? input.financialAssetsForDeeming + reverseMortgageLoanProceeds : input.financialAssetsForDeeming,
    };
};
